import { AxiosError } from 'axios';
import { ErrorCatcher } from './MessageError';

/**
 * Error `type` codes the Bikeleasing account-service can return that this app currently handles.
 * Deliberately scoped to codes actually consumed by a call site today, not the account-service's
 * full ~37-identifier catalog (most of which belong to registration/redemption/admin endpoints
 * probonio never calls) - extend when a new call site needs another code, re-verifying it against
 * the account-service source at that time (its public OpenAPI spec is confirmed to lag behind the
 * implementation for ~20 of 37 identifiers, so the spec alone isn't a reliable source of truth).
 * Verified directly against account-service source (EmployeeValidator/CompanyValidator +
 * EmployeeEmailAlreadyExistsException/EmployeeNotEligibleException/ParentCompanyDeletedException),
 * 2026-08-24.
 */
export const ACCOUNT_SERVICE_ERROR_TYPE = {
  EmailAlreadyExists: 'error:account@bikeleasing:email-already-exists',
  EmployeeNotEligible: 'error:account@bikeleasing:employee-not-eligible',
  ParentCompanyNotFound: 'error:account@bikeleasing:parent-company-not-found',
  ParentCompanyDeleted: 'error:account@bikeleasing:parent-company-deleted',
} as const;

export type AccountServiceErrorType = (typeof ACCOUNT_SERVICE_ERROR_TYPE)[keyof typeof ACCOUNT_SERVICE_ERROR_TYPE];

interface AccountServiceErrorData {
  type?: AccountServiceErrorType;
  eligibilityFailureReason?: string;
}

/**
 * Extracts the account-service error payload from an axios error, if present. probonio-api wraps
 * account-service errors one level deeper (`{ data: { data: { type, eligibilityFailureReason } } }`)
 * than services exposing the account-service response more directly (`{ data: { type, ... } }`) -
 * this checks both shapes.
 */
export function getAccountServiceErrorData(err: unknown): AccountServiceErrorData | undefined {
  if (!(err instanceof AxiosError)) return undefined;
  const body = err.response?.data as
    { type?: unknown; eligibilityFailureReason?: unknown; data?: { type?: unknown; eligibilityFailureReason?: unknown } } | undefined;
  const nested = body?.data;
  const type = nested?.type ?? body?.type;
  const eligibilityFailureReason = nested?.eligibilityFailureReason ?? body?.eligibilityFailureReason;
  if (typeof type !== 'string' && typeof eligibilityFailureReason !== 'string') return undefined;
  return {
    type: type as AccountServiceErrorType | undefined,
    eligibilityFailureReason: eligibilityFailureReason as string | undefined,
  };
}

export function getAccountServiceErrorType(err: unknown): AccountServiceErrorType | undefined {
  return getAccountServiceErrorData(err)?.type;
}

/**
 * Builds an ErrorCatcher (for useWithMessage) that maps known account-service error types to a
 * caller-supplied, context-specific display message. Unmapped/unknown types return undefined so
 * useWithMessage falls through to the next catcher / the generic default message.
 */
export function accountServiceErrorCatcher(messagesByType: Partial<Record<AccountServiceErrorType, string>>): ErrorCatcher {
  return err => {
    const type = getAccountServiceErrorType(err);
    return type ? messagesByType[type] : undefined;
  };
}
